/**
 * «NUMERAR AHORA» (Ajustes → Estructura → Campos y numeración, 23-10-2026).
 *
 * Junta la estructura, los valores ya guardados y las campañas, y dice qué
 * números escribir para un campo en un periodo. Las reglas de verdad (orden,
 * seguir o reiniciar, respetar lo escrito a mano) viven en `camposEstructura.ts`;
 * aquí solo se elige QUÉ sitios entran y con qué periodo se guardan:
 *
 *   · finca / nave / sector: todos los sitios vivos, guardados en el periodo
 *     elegido ('' si el campo es fijo).
 *   · cultivo: los ciclos que pisan el periodo elegido (una campaña o un año);
 *     se guardan con periodo '' porque el ciclo ya es de esa campaña. «Seguir»
 *     mira los ciclos del periodo anterior.
 *
 * PURO. Test: src/__tests__/planNumeracion.test.ts.
 */
import {
  type CampanaPeriodo,
  type CampoEstructura,
  type CicloOrden,
  type FincaOrden,
  type NaveOrden,
  type SectorOrden,
  type ValorEstructura,
  leerNumeracion,
  numerar,
  periodoAnterior,
  sitiosEnOrden,
} from "./camposEstructura";

export interface CicloRango extends CicloOrden {
  end_date: string | null;
}

export interface DatosEstructura {
  fincas: FincaOrden[];
  naves: NaveOrden[];
  sectores: SectorOrden[];
  ciclos: CicloRango[];
}

/** Las fechas que cubre un periodo, o null si no se conoce. */
export function rangoDePeriodo(
  periodo: string,
  campanas: ReadonlyArray<CampanaPeriodo>,
): { desde: string; hasta: string } | null {
  if (periodo.startsWith("anio:")) {
    const y = periodo.slice(5);
    return { desde: `${y}-01-01`, hasta: `${y}-12-31` };
  }
  if (periodo.startsWith("campana:")) {
    const c = campanas.find((x) => `campana:${x.id}` === periodo);
    return c ? { desde: c.start_date.slice(0, 10), hasta: c.end_date.slice(0, 10) } : null;
  }
  return null;
}

/** Los ciclos que pisan un rango de fechas (un ciclo sin fin sigue abierto). */
export function ciclosEnRango(ciclos: ReadonlyArray<CicloRango>, r: { desde: string; hasta: string }): CicloRango[] {
  return ciclos.filter((c) => c.start_date.slice(0, 10) <= r.hasta && (c.end_date === null || c.end_date.slice(0, 10) >= r.desde));
}

export interface PlanNumeracion {
  /** Lo que se escribe (nunca un valor escrito a mano). */
  escribir: { entidad_id: string; periodo: string; valor: string }[];
  /** Cuántos sitios entran. */
  total: number;
  /** Cuántos se respetan por estar escritos a mano. */
  manuales: number;
  /** Los cinco primeros en orden, para la vista previa: id y número que tendrá. */
  muestra: { entidad_id: string; valor: string; manual: boolean }[];
}

/**
 * El plan para numerar `campo` en `periodo`. Para un campo fijo de finca, nave o
 * sector el periodo es ''; para uno de cultivo es la campaña o el año de cuyos
 * ciclos se trata.
 */
export function planNumeracion(
  campo: CampoEstructura,
  periodo: string,
  datos: DatosEstructura,
  valores: ReadonlyArray<ValorEstructura>,
  campanas: ReadonlyArray<CampanaPeriodo>,
): PlanNumeracion {
  const cfg = campo.numeracion ?? leerNumeracion(null);
  const delCampo = valores.filter((v) => v.campo_id === campo.id);

  let sitios;
  let periodoGuardado: string;
  let anteriores: { entidad_id: string; valor: string }[] = [];
  const grupoDe = new Map<string, string | null>();

  if (campo.nivel === "cultivo") {
    const rango = rangoDePeriodo(periodo, campanas);
    const ciclos = rango ? ciclosEnRango(datos.ciclos, rango) : [];
    sitios = sitiosEnOrden("cultivo", { ...datos, ciclos });
    periodoGuardado = "";
    // La finca de TODOS los ciclos, también los de antes (alcance «finca»).
    for (const s of sitiosEnOrden("cultivo", datos)) grupoDe.set(s.id, s.grupo);
    const antes = periodoAnterior(periodo, campanas);
    const rangoAntes = antes ? rangoDePeriodo(antes, campanas) : null;
    if (rangoAntes) {
      const deAntes = new Set(ciclosEnRango(datos.ciclos, rangoAntes).map((c) => c.id));
      // Un ciclo largo pisa las dos campañas: no cuenta como «de antes».
      const ahora = new Set(sitios.map((s) => s.id));
      anteriores = delCampo.filter((v) => v.periodo === "" && deAntes.has(v.entidad_id) && !ahora.has(v.entidad_id));
    }
  } else {
    sitios = sitiosEnOrden(campo.nivel, datos);
    periodoGuardado = campo.vigencia === "fija" ? "" : periodo;
    for (const s of sitios) grupoDe.set(s.id, s.grupo);
    const antes = campo.vigencia === "fija" ? null : periodoAnterior(periodo, campanas);
    if (antes) anteriores = delCampo.filter((v) => v.periodo === antes);
  }

  const actuales = new Map(
    delCampo.filter((v) => v.periodo === periodoGuardado).map((v) => [v.entidad_id, { valor: v.valor, manual: v.manual }]),
  );
  const nuevos = numerar(sitios, cfg, actuales, anteriores, (id) => grupoDe.get(id) ?? null);
  const nuevoDe = new Map(nuevos.map((n) => [n.entidad_id, n.valor]));

  return {
    escribir: nuevos.map((n) => ({ entidad_id: n.entidad_id, periodo: periodoGuardado, valor: n.valor })),
    total: sitios.length,
    manuales: sitios.filter((s) => actuales.get(s.id)?.manual).length,
    muestra: sitios.slice(0, 5).map((s) => {
      const actual = actuales.get(s.id);
      return {
        entidad_id: s.id,
        valor: nuevoDe.get(s.id) ?? actual?.valor ?? "",
        manual: !!actual?.manual,
      };
    }),
  };
}

/**
 * El nombre de cada sitio para enseñarlo: «Mojonera», «San Carlos», «San Carlos ·
 * Puerta 7», «San Carlos · Puerta 7 · Melón». La finca no va delante de la nave:
 * la pantalla ya agrupa por finca.
 */
export function nombresDeSitios(datos: DatosEstructura): Map<string, string> {
  const m = new Map<string, string>();
  for (const f of datos.fincas) m.set(f.id, f.name);
  const nave = new Map(datos.naves.map((n) => [n.id, n.name]));
  for (const n of datos.naves) m.set(n.id, n.name);
  const sector = new Map<string, string>();
  for (const s of datos.sectores) {
    const nombre = `${nave.get(s.greenhouse_id) ?? ""} · ${s.name}`;
    sector.set(s.id, nombre);
    m.set(s.id, nombre);
  }
  for (const c of datos.ciclos) {
    const s = sector.get(c.block_id);
    if (s) m.set(c.id, c.crop_name ? `${s} · ${c.crop_name}` : s);
  }
  return m;
}
